
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AppInsightsService, ProviderService, ToastMessageService } from '@k12/app-services';
import { ToastMessageType , ProviderData, ProviderEnrollmentStatusModel, SubService, Employee, SeverityLevel } from '@k12/models';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,

  selector: 'lib-provider-details',
  standalone: true,
  imports: [MatFormFieldModule, MatInputModule, MatSelectModule, ReactiveFormsModule],
  templateUrl: './provider-details.component.html'
})
export class ProviderDetailsComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() providerData?: ProviderData;
  @Input() subServices: SubService[] = [];
  @Input() showReviewStatus: boolean = false;
  @Input() getTherapyEmployeeLength!: (employees: Employee[]) => number;
  @Input() getTutoringEmployeeLength!: (employees: Employee[]) => number;
  @Output() enrollmentStatuses = new EventEmitter<ProviderEnrollmentStatusModel[]>();

  // List of provider enrollment statuses
  providerEnrollmentStatuses: ProviderEnrollmentStatusModel[] = [];
  private readonly destroyRef = inject(DestroyRef);
  private readonly providerService = inject(ProviderService);
  private readonly appInsightsService = inject(AppInsightsService);
  private readonly toastMessageService = inject(ToastMessageService);

  get isTransportation(): boolean {
    return this.providerData?.type === 'Transportation';
  }

  get isTherapy(): boolean {
    return this.providerData?.type === 'Therapy';
  }

  get selectedServices(): string | string[] {
    const subjects = this.providerData?.subjects ?? [];
    if (this.isTherapy) {
      return subjects[0] ?? '';
    }
    return subjects;
  }
  ngOnInit(): void {
    this.loadProviderEnrollmentStatuses();
  }

  private loadProviderEnrollmentStatuses(): void {
    this.providerService.getProviderEnrollmentStatuses()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe({
      next: (response) => {
        this.providerEnrollmentStatuses = response;
        this.enrollmentStatuses.emit(response);
        // Set form status from enrollmentStatus string name
        // Remove Spaces from enrollmentStatus string name before matching
        if(this.providerData?.enrollmentStatus) {
          const matchingStatus = this.providerEnrollmentStatuses.find(
            (status) => status.name.toLowerCase().replace(/\s+/g, '') === this.providerData?.enrollmentStatus?.toLowerCase().replace(/\s+/g, '')
          );
          if (matchingStatus) {
            this.formGroup.controls['providerEnrollmentStatusId'].setValue(matchingStatus.id);
          }
        } 
      },
      error: (error: unknown) => {
          this.providerEnrollmentStatuses = [];
          const err = error instanceof Error ? error : new Error(String(error));
          this.appInsightsService.log(err, SeverityLevel.error);
          this.toastMessageService.showToast(
            ToastMessageType.Error,
            'Failed to load provider enrollment statuses. Status cannot be changed until this is resolved.'
          );
        }
    });
  }

  employeesDisplay(): string {
    if (this.isTransportation) {
      const count =
        this.providerData?.employees?.filter((employee) => employee.employeeType === 'Transportation').length ?? 0;
      return `Transportation (${count})`;
    }
    return (
      'Therapy (' +
      this.getTherapyEmployeeLength(this.providerData?.employees || []) +
      '), Tutoring (' +
      this.getTutoringEmployeeLength(this.providerData?.employees || []) +
      ')'
    );
  }
}
